import React, {createRef} from 'react';
import autobind from 'autobind-decorator';
// import { tileLayer as    LeafletTileLayer } from 'leaflet';
import {
    control as LeafletControl,
    latLngBounds as LeafletLatLngBounds,
    map as LeafletMap,
    marker as LeafletMarker,
    circle as Circle
} from 'leaflet';
import {basemapLayer} from 'esri-leaflet';

import {ConfirmationDialog} from '../ConfirmationDialog';
import {ObjectUtils} from '../../../Utils/ObjectUtils';

import {ARCGIS_TOKEN} from '../../../Constants';

export class SbMap extends React.Component {
    static GetMapTypes() {
        return [
            {
                label: 'Topography',
                mapType: 'Topographic'
            },
            {
                label: 'Satellite',
                mapType: 'ImageryClarity'
            },
            {
                label: 'Streets',
                mapType: 'Streets'
            },
            /*'Streets',
            'Topographic',
            'NationalGeographic',
            'Oceans',
            'Gray',
            'DarkGray',
            'Imagery',
            'ImageryClarity',
            'ImageryFirefly',
            'ShadedRelief',
            'Terrain',
            'USATopo',
            'Physical'*/
        ];
    }

    constructor(props) {
        super(props);

        this.mapRef = props?.ref ?? createRef();

        this.availableLayers = SbMap.GetMapTypes().map((map) => {
            return {
                label: map.label,
                mapType: map.mapType,
                layer: basemapLayer(this.getBasemapLayerType(map.mapType), {token: ARCGIS_TOKEN != "" ? ARCGIS_TOKEN : null})
            }
        });

        this.state = {
            map: null,
            popup: null,
            mapType: this.props.MapType,
            zoom: this.props.Zoom ?? 8,
        };

        this.zoomPanOptions = {
            animate: true,
            duration: .75
        };
    }

    @autobind
    getLatitude() {
        return this.props.Lat ?? 45.5;
    }

    @autobind
    getLongitude() {
        return this.props.Lng ?? -122.6;
    }

    @autobind
    getMapPosition() {
        return [this.getLatitude(), this.getLongitude()];
    }

    @autobind
    componentDidMount() {

        let selectedBaseMap = this.getSelectedBaseMap();

        let map = LeafletMap(this.mapRef.current, {
            layers: [selectedBaseMap.layer]
        }).setView(
            this.getMapPosition(),
            this.state.zoom
        );

        this.layerControl = LeafletControl.layers(
            this.availableLayers.reduce((map, obj) => ({...map, [obj.label]: obj.layer}), {})
        ).addTo(map);

        map.on('click', this.clickHandler);
        map.on('zoomend', this.zoomChangedHandler);
        map.on('moveend', this.locationChangedHandler);
        map.on('baselayerchange', this.mapTypeChangedHandler);

        this.setState({map}, () => {
            this.updateClickMarker();
            this.updateMarkers();
        });
    }

    componentDidUpdate(prevProps, prevState) {

        const zoomChanged = (this.props.Zoom !== prevProps.Zoom);
        const positionChanged = (prevProps.Lat !== this.getLatitude() || prevProps.Lng !== this.getLongitude());
        const mapTypeChanged = (this.props.MapType !== prevProps.MapType);

        if (zoomChanged && positionChanged) {
            this.updateMapPosition(this.props.Lat, this.props.Lng, this.props.Zoom);
        } else if (positionChanged) {
            this.updateMapPosition(this.props.Lat, this.props.Lng, null);
        } else if (zoomChanged) {
            this.updateMapPosition(null, null, this.props.Zoom);
        }

        if (!ObjectUtils.Compare(prevProps.ClickMarker, this.props.ClickMarker)) {
            this.updateClickMarker()
        }

        if (!ObjectUtils.Compare(prevProps.Markers, this.props.Markers, ObjectUtils.ReactElementReplacer)) {
            this.updateMarkers()
        }
        
        if (mapTypeChanged) {
            this.updateMapType();
        } 
    }

    @autobind
    updateMapPosition(lat, lng, zoom = null) {

        const position = [lat, lng];

        if(zoom != null && lat != null && lng != null) {
            this.state.map.flyTo(position, zoom, this.zoomPanOptions);
        }
        else if(lat != null && lng != null) {
            this.state.map.panTo(position, this.zoomPanOptions);
        } else if (zoom != null) {
            this.state.map.setZoom(zoom, this.zoomPanOptions);
        }
    }
    
    @autobind
    updateMapType() {
        this.getSelectedBaseMap()?.layer?.addTo?.(this.state.map);
    }

    @autobind
    updateClickMarker() {

        if(this.clickMarker) {
            this.state.map.removeLayer(this.clickMarker);
        }

        if(this.props.ClickMarker) {
            const clickMarkerPosition = [this.props.ClickMarker.lat, this.props.ClickMarker.lng];
            this.clickMarker = LeafletMarker(clickMarkerPosition).addTo(this.state.map);
        }
    }

    @autobind
    updateMarkers() {

        const map = this.state.map;

        if(this.markerArray) {
            for (const marker of this.markerArray) {
                map.removeLayer(marker);
            }
        }

        if(this.props.Markers) {
            let markerArray = [];
            let markerLatLngs = []; // used to get bounds

            this.props.Markers.forEach((x) => {
                if(x?.marker !== 'pin' && x?.lat != null && x?.lng != null) {
                    const marker = Circle([x.lat, x.lng], {
                            radius: x?.['accuracy'] ?? 1609.34,
                            ...(x?.muted ? {color: '#8C8C8C', fillColor: '#8C8C8C'} : {}),
                        })
                        .addTo(map)
                        .on('click', () => {
                            this.setState({
                                popup: x.content
                            });
                        });
                    markerArray.push(marker);
                }
                if(x?.marker !== 'circle' && x?.lat != null && x?.lng != null)  {
                    const marker = LeafletMarker([x.lat, x.lng], x?.muted ? {opacity: 0.6} : {})
                        .addTo(map)
                        .on('click', () => {
                            this.setState({
                                popup: x.content
                            });
                        });
                    if(x?.muted) {
                        marker.getElement()?.classList.add('sb-map-marker-muted');
                    }
                    markerArray.push(marker);
                    markerLatLngs.push([x.lat, x.lng]);
                }
            });

            // fit all markers if there is more than one. Else just fall back to default zoom level
            if(markerLatLngs.length > 1) {
                let bounds = LeafletLatLngBounds(markerLatLngs);
                map.fitBounds(bounds, { padding: [50, 50], ...this.zoomPanOptions });
            }

            this.markerArray = markerArray;
        }
    }

    @autobind
    getBasemapLayerType(value) {
        if(value != null && SbMap.GetMapTypes().find((x) => { return x.mapType === value; } ) !== undefined) {
            return value;
        }

        return 'Topographic';
    }

    @autobind
    getSelectedBaseMap() {
        let availableMapTypes = this.availableLayers;

        if(availableMapTypes.length > 0) {

            let baseMap = availableMapTypes.find((x) => {
                return x.label === this.props.MapType;
            });

            if (baseMap === undefined) {
                return availableMapTypes[0];
            }

            return baseMap;
        }

        return null;
    }

    @autobind
    clickHandler(e) {
        if(this.props.onClick) {
            this.props.onClick(e);
        } else {
            const latlng = e.latlng;
            this.setState({ markerLocation: {lat: latlng.lat, lng: latlng.lng} });
        }
    }

    @autobind
    zoomChangedHandler(e) {
        if(this.props.onZoomChanged) {
            this.props.onZoomChanged(e);
        } else {
            const zoom = e.target.getZoom();
            this.setState({ zoom: zoom });
        }
    }

    @autobind
    locationChangedHandler(e) {
        if(this.props.onLocationChanged) {
            this.props.onLocationChanged(e);
        } else {
            const latlng = e.target.getCenter();
            this.setState({ mapLocation: {lat: latlng.lat, lng: latlng.lng} });
        }
    }
    
    @autobind
    mapTypeChangedHandler(e) {
        if(this.props.onMapTypeChanged) {
            this.props.onMapTypeChanged(e?.name);
        } else {
            this.setState({ mapType: e?.name });
        }
    }

    @autobind
    render() {
        let style = {};
        if(this.props.style) {
            style = this.props.style;
        }

        // Note that while this component "renders"
        // here in this method, it doesn't actually
        // create or update the leaflet map until
        // componentDidMount and co

        return <div className="sb-map" style={style}>
            <ConfirmationDialog
                Title="Map Item"
                Body={this.state.popup}
                ConfirmationButtonMessage="Close"
                Display={this.state.popup !== null}
                OnConfirm={() => this.setState({popup: null})}
            />

            <div ref={this.mapRef} />
        </div>;
    }
}


